"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { storyblokEditable, StoryblokComponent } from "@storyblok/react/rsc";
import Autoplay from "embla-carousel-autoplay";
import { useLightboxOpen } from "@/components/layout/LightboxProvider";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  GalleryNavPrevious,
  GalleryNavNext,
  type CarouselApi,
} from "@/components/ui/carousel";

type QuoteGalleryStoryblok = {
  headline?: string;
  items?: any[];
  _uid: string;
  component: "quote_gallery";
  [k: string]: any;
};

type QuoteGalleryProps = {
  blok: QuoteGalleryStoryblok;
};

const QuoteGallery = ({ blok }: QuoteGalleryProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [canScroll, setCanScroll] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const lightboxOpen = useLightboxOpen();
  const autoplayRef = useRef(
    Autoplay({
      delay: 3000,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    }),
  );

  const checkCanScroll = useCallback(() => {
    if (!api) return;
    setCanScroll(api.canScrollNext() || api.canScrollPrev());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    checkCanScroll();
    api.on("resize", checkCanScroll);
    api.on("reInit", checkCanScroll);
    return () => {
      api.off("resize", checkCanScroll);
      api.off("reInit", checkCanScroll);
    };
  }, [api, checkCanScroll]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const autoplay = autoplayRef.current;
    if (!api) return;
    if (canScroll && isInView && !lightboxOpen) {
      autoplay.play();
    } else {
      autoplay.stop();
    }
  }, [api, canScroll, isInView, lightboxOpen]);

  return (
    <div ref={sectionRef} {...storyblokEditable(blok)} className="w-full">
      <section className="py-8 flex flex-col gap-4 w-full items-center">
        {blok.headline && (
          <h2 className="text-headline-md text-black text-left md:text-center w-full px-4 md:px-8 max-w-7xl mb-2">
            {blok.headline}
          </h2>
        )}

        <Carousel
          setApi={setApi}
          opts={{
            align: "center",
            loop: false,
            dragFree: false,
            watchResize: true,
            breakpoints: {
              "(min-width: 768px)": {
                align: "start",
                dragFree: true,
              },
            },
          }}
          plugins={[autoplayRef.current]}
          className="w-full relative"
        >
          <GalleryNavPrevious show={canScroll} />
          <CarouselContent
            className={
              canScroll
                ? "ml-0 pl-4 md:pl-8 items-stretch"
                : "justify-center items-stretch"
            }
          >
            {blok.items?.map((nestedBlok: any) => (
              <CarouselItem
                key={nestedBlok._uid}
                className={
                  canScroll
                    ? "pl-4 basis-auto h-auto"
                    : "basis-auto px-2 h-auto"
                }
              >
                <StoryblokComponent blok={nestedBlok} />
              </CarouselItem>
            ))}
            {canScroll && <div className="shrink-0 w-4 md:w-8" />}
          </CarouselContent>
          <GalleryNavNext show={canScroll} />
        </Carousel>
      </section>
    </div>
  );
};

export default QuoteGallery;
